import cx from "classnames"
import PropTypes from "prop-types"
import { useState, useEffect, useMemo, useCallback } from "react"
import Link from "components/Link"
import useIntersect from "effects/useIntersect"
import { useAnnouncement, useHeaderNavigation } from "effects/useSiteContent"
import { trackEvent } from "lib/googleTagManager"
import ContactSalesModal from "components/ContactSalesModal"
import Announcement from "./Announcement"
import Banner from "./Banner"
import closeIcon from "./images/close.svg"
import darkLogo from "./images/preset-logo-dark.svg"
import lightLogo from "./images/preset-logo-light.svg"
import darkToggleMenuIcon from "./images/toggle-menu-dark.svg"
import lightToggleMenuIcon from "./images/toggle-menu-light.svg"
import NavItem from "./NavItem"
import NavMobile from "./NavMobile"
import SignUpButton from "components/ui/SignUpButton"

function Header({
  dark,
  floating,
  trackSignupButtonClick,
  pageName,
  pageBanner,
}) {
  /** Header navigation from local YAML */
  const navItems = useHeaderNavigation()

  /** Announcement bar from local YAML */
  const announcement = useAnnouncement()

  /** Show announcement bar.
   *
   *  Default to "visible if configured" so the bar is present on the
   *  very first paint (SSR + first client render) and the layout
   *  doesn't shift when the dismissal check runs. Returning visitors
   *  who have dismissed see the bar briefly until the post-hydration
   *  effect hides it — small shift on a screen they've already
   *  engaged with, vs. a shift on every fresh visit. */
  const [announcementDismissed, setAnnouncementDismissed] = useState(false)
  useEffect(() => {
    if (
      announcement?.id &&
      announcement.id === localStorage.getItem("announcement_bar")
    ) {
      setAnnouncementDismissed(true)
    }
  }, [announcement?.id])
  const showAnnouncement = Boolean(announcement?.id) && !announcementDismissed

  const hideAnnouncement = () => {
    setAnnouncementDismissed(true)
    if (announcement?.id) {
      localStorage.setItem("announcement_bar", announcement.id)
    }
  }

  /** Show banner on top — same SSR-first / dismissal-after pattern
   *  as the announcement bar above. */
  const bannerName = pageName ? pageName.replace(/-/g, "_") : ""

  const [bannerDismissed, setBannerDismissed] = useState(false)
  useEffect(() => {
    if (pageBanner) {
      const { bannerId } = pageBanner
      if (
        bannerId &&
        bannerId === localStorage.getItem(`${bannerName}_banner_id`)
      ) {
        setBannerDismissed(true)
      }
    }
  }, [bannerName, pageBanner])
  const showBanner = Boolean(pageBanner?.bannerId) && !bannerDismissed

  const hideBanner = () => {
    const { bannerId } = pageBanner
    setBannerDismissed(true)
    localStorage.setItem(`${bannerName}_banner_id`, bannerId)
  }

  /** Mobile toggle */
  const [isOpen, setIsOpen] = useState(false)
  const toggleMenu = () => {
    setIsOpen(!isOpen)
  }

  /** Contact Sales modal */
  const [isContactModalOpen, setIsContactModalOpen] = useState(false)
  const sourcePage =
    typeof window !== "undefined" ? window.location.pathname : ""
  const openContactModal = useCallback(() => {
    setIsContactModalOpen(true)
    setIsOpen(false) // Close mobile menu if open
    // Track modal open for GA4/Segment
    trackEvent("contactSalesModalOpen", {
      sourcePage,
    })
  }, [sourcePage])
  const closeContactModal = useCallback(() => {
    setIsContactModalOpen(false)
  }, [])

  /** Intersection observer */
  const [observer, intersecting] = useIntersect()

  /** Header theme - derived value, not a function to avoid re-computation */
  const isLightBackground = intersecting || !dark || isOpen

  /** Memoize nav items to prevent unnecessary re-renders */
  const leftNavItems = useMemo(
    () => navItems?.filter(({ position }) => position.toUpperCase() === "LEFT"),
    [navItems]
  )
  const rightNavItems = useMemo(
    () =>
      navItems?.filter(({ position }) => position.toUpperCase() === "RIGHT"),
    [navItems]
  )

  const headerClassName = cx("w-full transition-all sticky top-0 left-0 z-20", {
    "text-white": !isLightBackground,
    "text-black": isLightBackground,
    "bg-gradient-to-b from-white to-white shadow-simple lg:py-1":
      intersecting || isOpen,
    "bg-gradient-to-b from-white via-white to-transparent-white":
      !intersecting && !isOpen && !floating,
    "lg:py-10": !intersecting,
  })

  const navClassName = cx(
    "hidden flex-1 items-center justify-between space-x-9 font-bold lg:flex",
    intersecting ? "text-base" : "text-base xl:text-xl"
  )

  const logoClassName = cx("h-10", intersecting ? "lg:h-9" : "lg:h-9 xl:h-11")

  return (
    <>
      {showAnnouncement && announcement && (
        <Announcement
          id={announcement.id}
          text={announcement.text}
          ctaText={announcement.ctaText}
          ctaLink={announcement.ctaLink}
          onClick={hideAnnouncement}
        />
      )}
      {showBanner && (
        <Banner
          id={pageBanner.bannerId}
          text={pageBanner.bannerText}
          link={pageBanner.bannerLink}
          onClick={hideBanner}
        />
      )}
      <header className={headerClassName}>
        <div className="relative flex items-center justify-between px-5 py-5 lg:container lg:py-0">
          {/* Logo */}
          <div className="w-full max-w-[250px]">
            <Link href="/" title="Home">
              <img
                className={logoClassName}
                src={isLightBackground ? darkLogo : lightLogo}
                alt="Preset logo"
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className={navClassName}>
            <ul className="flex items-center space-x-9">
              {leftNavItems?.map((props) => (
                <NavItem
                  key={props.label}
                  pos="LEFT"
                  onContactSalesClick={openContactModal}
                  {...props}
                />
              ))}
            </ul>
            <ul className="flex items-center space-x-9">
              {rightNavItems?.map((props) => (
                <NavItem
                  key={props.label}
                  pos="RIGHT"
                  onContactSalesClick={openContactModal}
                  {...props}
                />
              ))}
              <li>
                <SignUpButton
                  size={intersecting ? "small" : "flexible"}
                  dark={!isLightBackground}
                  trackClick={trackSignupButtonClick}
                >
                  Try for Free
                </SignUpButton>
              </li>
            </ul>
          </nav>

          {/* Mobile Menu Toggle */}
          <button
            className="flex h-10 w-10 cursor-pointer items-center justify-center lg:hidden"
            onClick={toggleMenu}
            type="button"
          >
            {isOpen ? (
              <img src={closeIcon} alt="Close" />
            ) : (
              <img
                src={
                  isLightBackground ? darkToggleMenuIcon : lightToggleMenuIcon
                }
                alt="Menu toggle"
              />
            )}
          </button>
        </div>
        {isOpen && (
          <NavMobile
            items={navItems}
            trackSignupButtonClick={trackSignupButtonClick}
            onContactSalesClick={openContactModal}
          />
        )}
      </header>
      <div ref={observer} />
      <ContactSalesModal
        isOpen={isContactModalOpen}
        onClose={closeContactModal}
        sourcePage={sourcePage}
      />
    </>
  )
}

Header.propTypes = {
  /** If true, the default state will be dark (white logo and text) */
  dark: PropTypes.bool,
  /** If true, the header will have a white to transparent gradient background */
  floating: PropTypes.bool,
  /** Signup button click tracking event */
  trackSignupButtonClick: PropTypes.string,
  /** Page name */
  pageName: PropTypes.string,
}

export default Header
